import { normalize } from './formatters';

// retire les guillemets et apostrophes qui entourent les valeurs CSV
export function cleanCell(str = '') {
  return str
    .toString()
    .trim()
    .replace(/^"+|"+$/g, '')
    .replace(/^'+|'+$/g, '');
}

// détection auto du rôle d'une colonne CSV, avec profils
export function detectRole(header, profile = 'bricoCasto') {
  const h = normalize(header);
  const hCompact = h.replace(/[\s\-_]/g, ''); // enlève espaces / - / _


  // Expéditeur / magasin / transporteur
  if (
    h.includes('expediteur') ||
    h.includes('expéditeur') ||
    h.includes('magasin') ||
    h.includes('transporteur')
  ) return 'sender';

  // Référence de la tâche / référence client / numéro commande / id
  if (
    (h.includes('reference') && (h.includes('tache') || h.includes('tâche') || h.includes('task'))) ||
    h.includes('reference client') ||
    h.includes('ref client') ||
    h.includes('id commande') ||
    h.includes('num commande') ||
    h === 'id'
  ) return 'taskRef';

  // Poids de reprise du PDL (avant la règle weight générique)
  if (h.includes('poids') && h.includes('reprise')) return 'reprise';

  // Poids, y compris "Poids de livraison du PDL"
  if (h === 'poids' || h.includes('poids')) return 'weight';

  // Type de service : rôle spécial pour Brico/Casto
  if (h.includes('type de service')) {
    if (profile === 'bricoCasto') return 'serviceType';
    return 'categories';
  }

  // Catégories / services (profil générique)
  if (
    h.includes('categorie') ||
    h.includes('catégorie') ||
    h.includes('service') ||
    h === 'services'
  ) return 'categories';

  // Début / horaire début
  if (h.includes('debut') || h.includes('début') || h.includes('heure debut') || h.includes('horaire debut'))
    return 'start';

  // Fin / horaire fin
  if (h === 'fin' || h.includes('fin') || h.includes('heure fin') || h.includes('horaire fin'))
    return 'end';

  // Représentant du client / client / contact
  if (
    h.includes('representant du client') ||
    h.includes('représentant du client') ||
    h.includes('contact client') ||
    h === 'client' ||
    h.includes('client ')
  ) return 'clientRep';

  // Ville classique
  if (h === 'ville' || h.includes('ville') || h.includes('city') || h.includes('localite') || h.includes('localité'))
    return 'city';

  // Adresse -> ville uniquement pour le profil Leroy Merlin
  if (profile === 'leroy' && h.includes('adresse'))
    return 'city';

  // BackOffice (Leroy Merlin) -> sert à taguer (R) / (P)
  // IMPORTANT: ignorer BackOfficesGroup, sinon ça écrase BackOffice avec "N/A"
  if (hCompact.includes('backofficesgroup')) return null;
  if (hCompact === 'backoffice' || hCompact.includes('backoffice')) return 'backOffice';

  // Statut (GOXL) → note/catégorie
  if (h === 'statut' && profile === 'goxl') return 'categories';

  // Date (GOXL) → ignoré
  if (h === 'date') return null;

  return null;
}

export function extractCity(address = '') {
  if (!address) return '';
  const clean = address.trim();

  // priorité : après la dernière virgule
  if (clean.includes(',')) {
    const parts = clean.split(',');
    return parts[parts.length - 1].trim();
  }

  // sinon : après un code postal 5 chiffres
  const match = clean.match(/\b\d{5}\b\s*(.+)$/i);
  if (match) {
    return match[1].trim();
  }

  return clean;
}

export function parseCSVProper(text) {
  const sep = text.slice(0, 2000).includes(';') ? ';' : ',';
  const rows = parseRawRows(text, sep);

  // --- Filet de sécurité : certains exports (observé avec Safari/macOS) ---
  // arrivent sans AUCUN caractère de fin de ligne (ni \n, ni \r, ni \r\n).
  // Dans ce cas, parseRawRows renvoie une seule "ligne" géante contenant
  // tous les champs de toutes les lignes concaténés. On le détecte et on
  // reconstruit les lignes en regroupant les champs par paquets de
  // `headerCount`, en se basant sur les en-têtes (toujours présents et
  // identifiables en tout début de texte).
  if (rows.length <= 1) {
    const recovered = recoverRowsWithoutLineBreaks(text, sep);
    if (recovered && recovered.length > 1) {
      return { sep, rows: recovered };
    }
  }

  return { sep, rows };
}

// Parsing CSV caractère par caractère, respectant les guillemets et gérant
// \n (Unix), \r\n (Windows) et \r seul (Mac classique) comme fins de ligne.
function parseRawRows(text, sep) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = i + 1 < text.length ? text[i + 1] : null;

    if (inQuotes) {
      if (ch === '"' && next === '"') { field += '"'; i++; }
      else if (ch === '"' && (next === sep || next === '\n' || next === '\r' || next === null)) { inQuotes = false; }
      else { field += ch; }
    } else {
      if (ch === '"') { inQuotes = true; }
      else if (ch === sep) {
        row.push(field.trim());
        field = '';
      } else if (ch === '\n' || ch === '\r') {
        // Gère \n (Unix), \r\n (Windows) et \r seul (Mac classique / certains exports GOXL)
        row.push(field.trim());
        if (row.some(c => c !== '')) rows.push(row);
        row = []; field = '';
        // si on a un \r suivi d'un \n, on saute le \n pour ne pas créer une ligne vide
        if (ch === '\r' && next === '\n') i++;
      } else {
        field += ch;
      }
    }
  }
  if (field !== '' || row.length > 0) {
    row.push(field.trim());
    if (row.some(c => c !== '')) rows.push(row);
  }
  return rows;
}

// Reconstruit les lignes d'un CSV qui n'a plus aucun séparateur de ligne
// (observé avec des exports GOXL téléchargés via Safari sur Mac : le texte
// arrive comme une seule "ligne" géante, parfois avec des en-têtes en fin
// de liste qui ont disparu en même temps que les retours à la ligne).
//
// Principe : on découpe tout le texte field par field (en respectant les
// guillemets, sans tenir compte des retours à la ligne puisqu'il n'y en a
// pas), puis on déduit la vraie taille d'une ligne de données en cherchant
// un diviseur du nombre de champs restants après les en-têtes visibles —
// plutôt que de supposer que cette taille est égale au nombre d'en-têtes
// (qui peut être inexact si un en-tête a disparu avec les retours ligne).
function recoverRowsWithoutLineBreaks(text, sep) {
  const allFields = splitAllFieldsIgnoringLineBreaks(text, sep);
  if (allFields.length < 2) return null;

  // Les en-têtes sont les champs qui précèdent la 1ère valeur entre
  // guillemets dans le texte original (les en-têtes GOXL ne sont pas
  // quotés, contrairement aux données). On les retrouve en comptant
  // combien de champs sont nécessaires pour couvrir le texte avant le
  // premier guillemet.
  const firstQuoteIdx = text.indexOf('"');
  if (firstQuoteIdx === -1) return null;
  const headerPart = text.slice(0, firstQuoteIdx).replace(/[;,]+$/, '');
  let headers = headerPart.split(sep).map((h) => h.trim()).filter((h) => h !== '');
  if (headers.length === 0) return null;

  // Le dernier champ "en-tête" peut être pollué par des guillemets collés
  // (ex: 'backOffice""') si un en-tête suivant a disparu en même temps que
  // le saut de ligne. On le nettoie pour l'affichage, mais on ne compte pas
  // sur lui pour déduire la taille de paquet.
  headers = headers.map((h) => h.replace(/"+$/g, '').trim());

  const dataFieldsCount = allFields.length - headers.length;
  if (dataFieldsCount <= 0) return null;

  // On cherche la taille de ligne la plus probable : un diviseur exact de
  // dataFieldsCount, proche du nombre d'en-têtes (à ±2 colonnes, pour
  // tolérer un en-tête disparu comme "Reprise").
  let packetSize = null;
  for (let delta = 0; delta <= 2 && packetSize === null; delta++) {
    for (const candidate of [headers.length - delta, headers.length + delta]) {
      if (candidate > 0 && dataFieldsCount % candidate === 0) {
        packetSize = candidate;
        break;
      }
    }
  }
  // Si aucun diviseur propre n'est trouvé, on retombe sur le nombre
  // d'en-têtes tel quel (mieux que rien, la dernière ligne sera incomplète).
  if (packetSize === null) packetSize = headers.length;

  const dataFields = allFields.slice(headers.length);
  const rows = [headers];
  for (let i = 0; i < dataFields.length; i += packetSize) {
    const chunk = dataFields.slice(i, i + packetSize);
    if (chunk.some((c) => c !== '')) rows.push(chunk);
  }

  return rows;
}

// Découpe tout le texte en champs CSV (en respectant les guillemets),
// sans aucun traitement des retours à la ligne — utilisé uniquement par le
// filet de sécurité ci-dessus, quand on sait déjà qu'il n'y a pas de
// terminateur de ligne exploitable dans le texte.
function splitAllFieldsIgnoringLineBreaks(text, sep) {
  const fields = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = i + 1 < text.length ? text[i + 1] : null;

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        const afterTwo = i + 2 < text.length ? text[i + 2] : null;
        if (afterTwo !== null && afterTwo !== sep && afterTwo !== '"') {
          // "" suivi d'un caractère normal = frontière entre deux lignes collées
          inQuotes = false;
          fields.push(field.trim());
          field = '';
          i++;
          inQuotes = true;
        } else {
          field += '"';
          i++;
        }
      }
      else if (ch === '"' && (next === sep || next === null)) { inQuotes = false; }
      else { field += ch; }
    } else {
      if (ch === '"') {
        if (field.length > 0) {
          // Texte non-quoté suivi d'un guillemet = frontière header/données
          fields.push(field.trim());
          field = '';
        }
        inQuotes = true;
      }
      else if (ch === sep) {
        fields.push(field.trim());
        field = '';
      } else {
        field += ch;
      }
    }
  }
  if (field !== '') fields.push(field.trim());
  return fields;
}
